import { useEffect, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import EditBusinessCellModal from './EditBusinessCellModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BusinessStatus, BusinessStatusColor, DashboardViewType, ToastType } from '../../../../helper/constant';
import { faArrowDown, faArrowUp, faCheckCircle, faEdit, faEye, faEyeSlash, faPauseCircle, faPen, faPencil, faStopCircle, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { useAppContext } from '../../../../context/app.context';
import CustomLoader from '../../../../components/common/CustomLoader';
import { deleteRowBusiness360CellAPI, getDashboardData, moveRowBusiness360CellAPI, updateShowHideBusiness360CellAPI } from '../../../../service/ai-model.service';

const EditableCell = ({ style,
    type,
    enableSort = false,
    enableDelete = false,
    value,
    summary,
    isStatus,
    reference,
    enableHideShow = false,
    show = true,
    enableShowMore = false
}) => {
    const [showModal, setShowModal] = useState(false);
    const [data, setData] = useState(value);
    const { business360EditMode, business360Data, updateBusiness360Data, activeDashboardVersion, showAlert } = useAppContext();
    const [isActive, setIsActive] = useState(false);
    const [loading, setLoading] = useState(null);
    const [showMore, setShowMore] = useState(false);

    useEffect(() => {
        if (value) {
            setData(value);
        }
        return () => {
            setData('')
        }
    }, [value]);

    useEffect(() => {
        setIsActive(show)
    }, [show])

    const getStatusIcon = (status) => {
        if (status === BusinessStatus.DONE) {
            return <FontAwesomeIcon icon={faCheckCircle} color={BusinessStatusColor.DONE} />
        }
        if (status === BusinessStatus.PENDING) {
            return <FontAwesomeIcon icon={faPauseCircle} color={BusinessStatusColor.PENDING} />
        }
        if (status === BusinessStatus.ERROR) {
            return <FontAwesomeIcon icon={faStopCircle} color={BusinessStatusColor.ERROR} />
        }
        return <></>
    }
    const tooltip = <Tooltip className='business-tooltip'><p dangerouslySetInnerHTML={{ __html: summary }}></p></Tooltip>;

    const updateHideShowCellStatus = () => {
        if (!reference) return;
        setLoading(true);
        const query = new URLSearchParams(window.location.search);
        const payload = {
            companyName: query.get('org') || '',
            versionId: activeDashboardVersion,
            reference: {
                products: reference.pi,
                opportunities: reference.oi,
                recommendations: reference.ri,
                showchild: !isActive
            },
        };
        updateShowHideBusiness360CellAPI(payload)
            .then(() => {
                const param = new URLSearchParams(window.location.search);
                const org = param.get('org');
                getDashboardData({ type: DashboardViewType.DASHBOARDS, org, versionId: activeDashboardVersion })
                    .then((result) => {
                        if (result && result.data) {
                            updateBusiness360Data([result.data]);
                            setIsActive(!isActive);
                        }
                    }).catch((e) => e)
                    .finally(() => setLoading(false));
            })
            .catch((err) => {
                showAlert('Error updating cell status', ToastType.ERROR);
                setLoading(false)
            })
    }

    const deleteRow = () => {
        if (!reference) return;
        setLoading(true);
        const query = new URLSearchParams(window.location.search);
        const payload = {
            companyName: query.get('org') || '',
            versionId: activeDashboardVersion,
            reference: {
                products: reference.pi,
                opportunities: reference.oi,
                recommendations: reference.ri
            },
            type
        };
        deleteRowBusiness360CellAPI(payload)
            .then(() => {
                const param = new URLSearchParams(window.location.search);
                const org = param.get('org');
                getDashboardData({ type: DashboardViewType.DASHBOARDS, org, versionId: activeDashboardVersion })
                    .then((result) => {
                        if (result && result.data) {
                            updateBusiness360Data([result.data]);
                            setIsActive(!isActive);
                        }
                    }).catch((e) => e)
                    .finally(() => setLoading(false));
            })
            .catch((err) => {
                showAlert('Error updating cell status', ToastType.ERROR);
                setLoading(false)
            })
    }

    const moveRow = (move) => {
        if (!reference) return;
        setLoading(true);
        const query = new URLSearchParams(window.location.search);
        const payload = {
            companyName: query.get('org') || '',
            versionId: activeDashboardVersion,
            reference: {
                products: reference.pi,
                opportunities: reference.oi,
                recommendations: reference.ri,
            },
            type,
            move
        };
        moveRowBusiness360CellAPI(payload)
            .then(() => {
                const param = new URLSearchParams(window.location.search);
                const org = param.get('org');
                getDashboardData({ type: DashboardViewType.DASHBOARDS, org, versionId: activeDashboardVersion })
                    .then((result) => {
                        if (result && result.data) {
                            updateBusiness360Data([result.data]);
                            setIsActive(!isActive);
                        }
                    }).catch((e) => e)
                    .finally(() => setLoading(false));
            })
            .catch((err) => {
                showAlert('Error updating cell status', ToastType.ERROR);
                setLoading(false)
            })
    }

    const updateShowMoreStatus = () => {
        if (!reference) return;
        const updatedData = business360Data.map(businessData => {
            const products = businessData.products.map((product, pi) => {
                if (pi === reference.pi) {
                    const opportunities = product.opportunities.map((opportunity, oi) => {
                        if (oi === reference.oi) {
                            const recommendations = opportunity.recommendations.map((recommendation, ri) => {
                                if (ri === reference.ri) {
                                    return {
                                        ...recommendation,
                                        showMore: !showMore
                                    }
                                }
                                return recommendation;
                            });
                            return {
                                ...opportunity,
                                recommendations
                            }
                        }
                        return opportunity;
                    });
                    return {
                        ...product,
                        opportunities
                    }
                }
                return product;
            });
            return {
                ...businessData,
                products
            }
        });
        updateBusiness360Data(updatedData);
        setShowMore(!showMore);
    }

    const renderCellContent = () => {
        return (
            <div className='p-1'>
                {!isStatus && <p className='m-0 p-0' dangerouslySetInnerHTML={{ __html: data }}></p>}
                {isStatus && <p className='m-0 p-0'>{getStatusIcon(data)}</p>}
                {(business360EditMode && enableHideShow) && <FontAwesomeIcon style={{ top: 5, right: 90, cursor: 'pointer', position: 'absolute', pointerEvents: 'auto' }} icon={isActive ? faEye : faEyeSlash} className='text-blue' onClick={() => { updateHideShowCellStatus() }} size='sm' />}
                {(business360EditMode && enableSort) && <FontAwesomeIcon style={{ top: 5, right: 70, cursor: 'pointer', position: 'absolute', pointerEvents: 'auto' }} icon={faArrowUp} className='text-blue' onClick={() => { moveRow('up') }} size='sm' />}
                {(business360EditMode && enableSort) && <FontAwesomeIcon style={{ top: 5, right: 50, cursor: 'pointer', position: 'absolute', pointerEvents: 'auto' }} icon={faArrowDown} className='text-blue' onClick={() => { moveRow('down') }} size='sm' />}
                {(business360EditMode && enableDelete) && <FontAwesomeIcon style={{ top: 5, right: 30, cursor: 'pointer', position: 'absolute', pointerEvents: 'auto' }} icon={faTrashCan} className='text-danger' onClick={() => { deleteRow() }} size='sm' />}
                {business360EditMode && <FontAwesomeIcon style={{ top: 5, right: 10, cursor: 'pointer', position: 'absolute', pointerEvents: 'auto' }} icon={faPencil} className='text-blue' onClick={() => { setShowModal(true) }} size='sm' />}
                {renderShowMore()}
            </div>
        )
    }

    const render = () => {
        if (summary) {
            return (
                <OverlayTrigger placement="right" overlay={tooltip} trigger={'click'} rootClose>
                    <div className={`d-flex align-items-center justify-content-center ${summary ? 'cursor-pointer' : ''} ${!show ? "editable-cell--disabled" : ""}`}>
                        {renderCellContent()}
                    </div>
                </OverlayTrigger>
            )
        }

        return (
            <div className={`d-flex align-items-center justify-content-center flex-column ${!show ? "editable-cell--disabled" : ""}`} >
                {renderCellContent()}
            </div>
        )
    }

    const renderShowMore = () => {
        if (!enableShowMore) return null;
        if (showMore) {
            return (
                <>
                    <p className='text-primary cursor-pointer m-0 p-0' onClick={() => updateShowMoreStatus()}> Show Less</p>
                </>
            )
        } else {
            return (
                <>
                    <p className='text-primary cursor-pointer m-0 p-0' onClick={() => updateShowMoreStatus()}> Show More</p>
                </>
            )
        }
    }

    return (
        <>
            {loading && <CustomLoader full />}
            {render()}
            <EditBusinessCellModal
                name={`Edit: ${reference?.key}`}
                data={data}
                isStatus={isStatus}
                reference={reference}
                showModal={showModal}
                onSave={content => { setData(content) }}
                setShowModal={setShowModal} />
        </>
    );
};

export default EditableCell;
